<template lang="pug">
modal(
  :modal-id="modalId",
  title="Contract",
  @shown="onShowModal",
  @hidden="onHideModal"
)
  template(#default)
    form(v-if="currentContract", novalidate)
      .mb-3
        label.form-label(for='email') Email address
        input#email.form-control(
          type='email',
          aria-describedby='emailHelp',
          v-model="currentContract.email",
          @blur="onEmailBlur"
        )
      .mb-3
        label.form-label(for='company') Company
        input#company.form-control(
          required,
          v-model="currentContract.code",
          type='text',
          aria-describedby='companyHelp',
          @blur="onCodeBlur"
        )
        #linkToContractHelp.form-text
          a.text-decoration-none(
            :class="{ disabled: !currentContract.code || errors.company, 'link-danger': errors.company }"
            :href="companyProfile",
            target="_blank"
          ) {{ currentContract.companyName || 'Open company page' }}
            font-awesome-icon.ms-1(:icon="['far','external-link']")
      .mb-3
        .input-group
          input.form-control(
            type='date',
            aria-label='From date',
            v-model="currentContract.startDate"
          )
          span.input-group-text -
          input.form-control(
            type='date',
            aria-label='To date',
            :min="currentContract.startDate",
            v-model="currentContract.endDate"
          )
      .my-4
        .form-check
          input#flexRadioDefault1.form-check-input(
            type='radio',
            name='flexRadioDefault',
            :checked="currentContract.contractType === 'month'",
            @change="selectContractType('month')"
          )
          label.form-check-label(for='flexRadioDefault1')
            | Monthly
        .form-check
          input#flexRadioDefault2.form-check-input(
            type='radio',
            name='flexRadioDefault',
            :checked="currentContract.contractType === 'year'",
            @change="selectContractType('year')"
          )
          label.form-check-label(for='flexRadioDefault2')
            | Yearly
      .mb-3
        label.form-label(for='price') Сontract value
        .input-group
          input#price.form-control.fs-1(
            type='number',
            aria-describedby='pricelHelp',
            v-model="currentContract.contractValue")
          span.input-group-text ₴
      .bg-light.rounded.p-3.mb-3(v-for="rule in currentContract.contractRules")
        .mb-3.d-flex
          a.link-dark.text-decoration-none(:href="getBotLink(rule)")
            img.me-1.align-baseline(
              :src="getIconSrcByPlatform(rule.platform)",
              style='height: 0.8rem'
            )
            span.font-monospace.small.text-muted.me-1 {{ rule.botId }}
            | {{ rule.name }}
          button.btn-close.ms-auto(type='button', @click="deleteRule(rule.botId)")
        .mb-3
          label.form-label(for='subscribers') Subscribers
          input#subscribers.form-control(
            type='number',
            aria-describedby='subscriberslHelp',
            v-model="rule.subscribers")
        .mb-3
          label.form-label(for='dailyPush') Daily push
          input#dailyPush.form-control(
            type='number',
            aria-describedby='dailyPushHelp',
            v-model="rule.dailyPush")
        .mb-3
          label.form-label(for='internalRequests') Internal API requests
          input#internalRequests.form-control(
            type='number',
            aria-describedby='internalRequestsHelp',
            v-model="rule.internalApiRequests")
        .mb-3
          label.form-label(for='externalRequests') External API requests
          input#externalRequests.form-control(
            type='number',
            aria-describedby='externalRequestsHelp',
            v-model="rule.externalApiRequests")
      .bg-light.rounded.p-3.mb-3
        select.form-select(aria-label='Bot select', @change="onSelectValueChange")
          option(v-for="opt in botSelectOptions", :value="opt.id") {{ opt.name }}
      .mb-3
        label.form-label(for='linkToContract') Link
        input#linkToContract.form-control(
          type='text',
          aria-describedby='linkToContractHelp',
          v-model="currentContract.documentLink"
        )
        #linkToContractHelp.form-text
          a.text-decoration-none(
            :class="{disabled: !currentContract.documentLink}",
            :href="currentContract.documentLink",
            target="_blank"
          ) Open document
            font-awesome-icon.ms-1(:icon="['far','external-link']")
      .mb-3(v-if="contractSelectOptions.length")
        label.form-label(for="version") Contracts
        select#version.form-select(aria-label="Select version", v-model="selectedContractId", @change="onContractSelect")
          template(v-for="option in contractSelectOptions")
            optgroup(v-if="option.type === 'group'", :label="option.name")
              option(
                v-for="opt in option.data",
                :value="opt.id"
              ) {{ opt.name }}
            option(
              v-else,
              :value="option.id"
            ) {{ option.name }}
  template(#footer)
    button.btn.btn-secondary(type='button', data-bs-dismiss='modal') Close
    button.btn.btn-primary(
      type='button',
      :disabled="isSubmitButtonDisabled",
      data-bs-dismiss='modal',
      @click="onSubmit"
    ) Save changes
</template>

<script>
import Modal from '@/components/base/Modal'

import { getBotsByEmail } from '@/services/api/bot.api'
import { getContractsByEmail } from '@/services/api/contract.api'
import { createRule } from '@/utils/factory/contract/rule'
import { createContract } from '@/utils/factory/contract/index'
import { getNameByCode, getIconSrcByPlatform } from '@/utils/helpers/contract'

export default {
  components: { Modal },
  props: {
    modalId: {
      type: String,
      required: true
    },
    contract: {
      type: Object,
      default: null
    },
    contractsHistory: {
      type: Array,
      default: () => []
    },
    chatbots: {
      type: Array,
      default: () => []
    }
  },
  emits: ['save', 'shown', 'hidden', 'changeEmail'],
  data() {
    return {
      errors: {
        company: false
      },
      currentContract: {},
      selectedContractId: ''
    }
  },
  computed: {
    companyProfile() {
      return this.currentContract.code
        ? `https://opendatabot.ua/c/${this.currentContract.code}`
        : ''
    },
    isSubmitButtonDisabled() {
      return !this.currentContract.startDate || !this.currentContract.endDate
    },
    botSelectOptions() {
      if (
        !this.chatbots ||
        !this.currentContract ||
        !this.currentContract.contractRules
      )
        return []
      return this.getSelectOptions()
    },
    contractSelectOptions() {
      if (!this.currentContract || !this.contractsHistory) return []
      return this.getContractSelectOptions()
    }
  },
  watch: {
    contract: {
      handler(newValue) {
        this.updateCurrentContract(newValue)
      },
      immdediate: true
    }
  },
  methods: {
    getIconSrcByPlatform,
    onShowModal(event) {
      this.updateCurrentContract(this.contract)
      this.$emit('shown', event)
    },
    onHideModal(event) {
      if (!this.contract.isEqual(this.currentContract)) {
        this.$emit('hidden', event)
      }
    },
    getBotLink(rule) {
      return `https://pipe.bot/${rule.platform}/${rule.botId}`
    },
    updateCurrentContract(contract) {
      this.errors.company = false
      this.currentContract = this.deepCloneContract(contract)
      this.selectedContractId = this.currentContract.id || ''
      if (!this.currentContract.companyName) this.errors.company = true
    },
    async onCodeBlur() {
      this.errors.company = false
      const { code } = this.currentContract
      const companyName = await getNameByCode(code)
      if (!companyName) {
        this.errors.company = true
      }
      this.currentContract.companyName = companyName
    },
    onEmailBlur() {
      const { email } = this.currentContract
      this.$emit('changeEmail', email)
    },
    deleteRule(botId) {
      this.currentContract.contractRules =
        this.currentContract.contractRules.filter(
          (rule) => rule.botId !== botId
        )
    },
    selectContractType(type) {
      this.currentContract.contractType = type
    },
    onSelectValueChange(event) {
      const selectedBotId = event.target.value
      if (selectedBotId) {
        this.addRule(+selectedBotId)
        event.target.selectedIndex = 0
      }
    },
    onContractSelect(event) {
      this.errors.company = false

      const contractId = event.target.value
      if (contractId) this.selectContractById(contractId)
      else this.createNewContract()

      if (!this.currentContract.companyName) this.errors.company = true
    },
    selectContractById(id) {
      const contract = this.contractsHistory.find((contr) => contr.id === id)
      if (contract) {
        this.currentContract.mergeContracts(contract)
      }
    },
    createNewContract() {
      const newContract = createContract()
      this.currentContract.mergeContracts(newContract)
    },
    addRule(botId) {
      const bot = this.chatbots.find((chatbot) => chatbot.id === botId)
      const newRule = createRule({
        botId,
        name: bot.name,
        platform: bot.platform
      })
      this.currentContract.addRule(newRule)
    },
    async getBotsByEmail(email) {
      const clientToken = this.$cookie.get('clientToken')
      const bots = await getBotsByEmail({
        clientToken,
        email
      })
      return bots.data.map((bot) => bot.attributes)
    },
    async getContractsByEmail(email) {
      const clientToken = this.$cookie.get('clientToken')
      const { data } = await getContractsByEmail({ email, clientToken })
      return data.map((contract) => createContract(contract.attributes))
    },
    onSubmit() {
      this.$emit('save', {
        id: this.currentContract.id,
        contract: this.prepareContractToSave(this.currentContract)
      })
    },
    prepareContractToSave(contract) {
      return contract.toJSON()
    },
    getSelectOptions() {
      let options = []
      const defaultOption = { id: '', name: 'Add chatbot' }
      options.push(defaultOption)
      const unavailableOptions = this.currentContract.contractRules.map(
        ({ botId }) => botId
      )
      options.push(
        ...this.chatbots
          .filter((bot) => !unavailableOptions.includes(bot.id))
          .map((bot) => {
            let name = `${bot.id} ${bot.name}`
            if (bot.platform) name += ` / ${bot.platform}`
            return { ...bot, name }
          })
      )
      return options
    },
    getContractSelectOptions() {
      const options = []
      if (this.contractsHistory.length) {
        const contracts = this.contractsHistory.map(this.getContractGroupValue)
        options.push({
          type: 'group',
          name: 'Contracts',
          data: [...contracts]
        })
      }
      options.push({ type: 'value', id: '', name: 'Create new' })
      return options
    },
    getContractGroupValue(contract) {
      const name = `${contract.endDate || ''} ${
        contract.companyName || contract.code
      }`
      return {
        type: 'value',
        id: contract.id,
        name
      }
    },
    deepCloneContract(contract) {
      return contract.deepClone()
    }
  }
}
</script>

<style lang="sass" scoped>
a.disabled
  pointer-events: none
  color: currentColor
  opacity: 0.7
</style>
